import React, { useMemo } from "react";
import { formatDate } from "date-fns";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { formatDateOnly } from "@helpers/date";
import { useEntitiesById } from "@hooks/useEntities";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { AccountCard } from "@components/financialAccount/AccountCard/AccountCard";
import { TransactionAmount } from "@components/transactions/TransactionAmount";
import { getLeadingAccountConnection } from "@common/helpers/financialAccount";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { TransactionListItemDto } from "@common/types/transaction";
import { HistoryCategory } from "@components/history/components/HistoryCategory";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { PairedTransactionData } from "./schema";
import styles from "./HistoryPairedTransferDetails.module.scss";

interface ActiveTransactionProps {
    transaction: PairedTransactionData & { amount: number };
}

export const ActiveTransaction: React.FC<ActiveTransactionProps> = ({
    transaction,
}) => {
    const entities = useEntitiesById();
    const categories = useCategoriesById();
    const financialAccounts = useFinancialAccounts();

    const financialAccount = transaction.financialAccountId
        ? financialAccounts.find(
              (fa) => fa.id === transaction.financialAccountId,
          )
        : undefined;
    const entity = transaction.entityId
        ? entities.get(transaction.entityId)
        : undefined;

    const categoryDisplay = useMemo(() => {
        if (!transaction.categoryId) {
            return null;
        }
        return (
            <HistoryCategory
                value={{
                    value: transaction.categoryId,
                    valueLabel: categories[transaction.categoryId]?.label,
                }}
                field="categoryId"
                resource={transaction}
                categories={categories}
            />
        );
    }, [transaction, categories]);

    const description =
        transaction.displayDescription ?? transaction.bankDescription;
    let formattedDate = "";
    if (transaction.dateOnly) {
        formattedDate = formatDateOnly(
            transaction.dateOnly,
            fullDateFullYearFormat,
        );
    } else if (transaction.date) {
        formattedDate = formatDate(transaction.date, fullDateFullYearFormat);
    }
    const connection = financialAccount
        ? (getLeadingAccountConnection(financialAccount) as
              | IntegrationConnection
              | undefined)
        : undefined;

    return (
        <div className={styles.transactionContent}>
            <div className={styles.dateColumn}>{formattedDate}</div>
            <div className={styles.descriptionColumn}>
                <BreakableText
                    className={styles.description}
                    text={description}
                    showTooltip
                />
            </div>
            <div className={styles.categoryColumn}>{categoryDisplay}</div>
            <div className={styles.accountColumn}>
                {financialAccount && (
                    <AccountCard
                        account={financialAccount}
                        connection={connection}
                        onlyNumber
                        showType={false}
                        iconSize="xs"
                        maxNameLength={12}
                    />
                )}
            </div>
            <div className={styles.amountColumn}>
                <TransactionAmount
                    transaction={
                        {
                            amount: transaction.amount,
                            originalAmount:
                                transaction.originalAmount ??
                                transaction.amount,
                        } as TransactionListItemDto
                    }
                    addSign
                    color
                />
            </div>
            <div className={styles.entityColumn}>
                {entity && <EntityAvatar entity={entity} size="xs" />}
            </div>
        </div>
    );
};
